import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Banner, Button, Card, Field, Input, Select } from "@magarac/ui";
import {
  settingsSchedulingSchema,
  type SettingsScheduling,
} from "@magarac/shared/schemas/settings";
import { HelpIcon } from "../../../components/HelpIcon";
import { messageFor } from "../../../lib/errors";
import { useSetting } from "../../../lib/settings";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/settings/scheduling")({
  component: SchedulingPage,
});

const ZONES =
  typeof Intl.supportedValuesOf === "function"
    ? Intl.supportedValuesOf("timeZone").filter(
        (z) => z.startsWith("America/") || z.startsWith("Pacific/Honolulu") || z === "UTC",
      )
    : [
        "America/New_York",
        "America/Chicago",
        "America/Denver",
        "America/Phoenix",
        "America/Los_Angeles",
        "America/Anchorage",
        "Pacific/Honolulu",
      ];

/** SCH-4, SCH-6 knobs (settings.scheduling). Screen key: settings.scheduling */
function SchedulingPage() {
  const { active } = useTenant();
  const { query, save, value } = useSetting("scheduling");
  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Scheduling</h1>
        <HelpIcon screen="settings.scheduling" />
      </div>
      {query.isPending ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <SchedulingForm
          key={active?.id ?? "none"}
          initial={
            value ?? {
              timezone: "America/Chicago",
              windowStepMin: 60,
              autoAssign: "off",
              defaultTravelPadMin: 20,
            }
          }
          onSave={(v) => save.mutateAsync(v)}
          busy={save.isPending}
          error={save.error ? messageFor(save.error) : null}
          saved={save.isSuccess}
        />
      )}
    </div>
  );
}

function SchedulingForm({
  initial,
  onSave,
  busy,
  error,
  saved,
}: {
  initial: SettingsScheduling;
  onSave: (v: SettingsScheduling) => Promise<unknown>;
  busy: boolean;
  error: string | null;
  saved: boolean;
}) {
  const [form, setForm] = useState<SettingsScheduling>(initial);
  const [localError, setLocalError] = useState<string | null>(null);
  const submit = () => {
    const parsed = settingsSchedulingSchema.safeParse(form);
    if (!parsed.success) {
      setLocalError("Please check the values.");
      return;
    }
    setLocalError(null);
    void onSave(parsed.data);
  };
  return (
    <>
      {localError || error ? <Banner tone="danger">{localError ?? error}</Banner> : null}
      {saved ? <Banner tone="ok">Saved.</Banner> : null}
      <Card>
        {form.timezone !== initial.timezone ? (
          <Banner tone="warn">
            Visits already booked stay at the same moment, so their clock times will show
            differently in {form.timezone}. Check the calendar after saving.
          </Banner>
        ) : null}
        <Field id="timezone" label="Time zone" hint="All visit times are shown in this zone.">
          <Select
            id="timezone"
            value={form.timezone}
            onChange={(e) => setForm({ ...form, timezone: e.target.value })}
          >
            {ZONES.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          id="step"
          label="Arrival window start times"
          hint="How often a new arrival window can start."
        >
          <Select
            id="step"
            value={String(form.windowStepMin)}
            onChange={(e) =>
              setForm({ ...form, windowStepMin: Number(e.target.value) as 15 | 30 | 60 })
            }
          >
            <option value="60">Every hour (8:00, 9:00, 10:00…)</option>
            <option value="30">Every 30 minutes</option>
            <option value="15">Every 15 minutes</option>
          </Select>
        </Field>
        <Field
          id="assign"
          label="How visits are assigned"
          hint="Choose who does each visit yourself, or let us pick the least busy person."
        >
          <Select
            id="assign"
            value={form.autoAssign}
            onChange={(e) =>
              setForm({ ...form, autoAssign: e.target.value as "off" | "least_loaded" })
            }
          >
            <option value="off">I assign visits myself (they arrive unassigned)</option>
            <option value="least_loaded">Assign automatically to the least busy person</option>
          </Select>
        </Field>
        <Field
          id="pad"
          label="Drive time when we cannot look up an address (minutes)"
          hint="Used only when the map lookup fails."
        >
          <Input
            id="pad"
            type="number"
            inputMode="numeric"
            min={0}
            max={120}
            value={form.defaultTravelPadMin}
            onChange={(e) => setForm({ ...form, defaultTravelPadMin: Number(e.target.value) })}
          />
        </Field>
        <Button onClick={submit} busy={busy}>
          Save
        </Button>
      </Card>
    </>
  );
}
